import { useRef, useEffect, useState, useCallback } from 'react';
import mapboxgl from 'mapbox-gl';
import { Box, IconButton } from '@mui/material';
import { BiLayer } from 'react-icons/bi';

const mapStyles = [
  'mapbox://styles/safet-fish/cmb9v575b00us01s6ctg4ewb3',
  'mapbox://styles/mapbox/satellite-v9'
];

const conferenceCountries = [
  { name: 'Philippines', coordinates: [121.774, 12.8797] },
  { name: 'Indonesia', coordinates: [113.9213, -0.7893] },
  { name: 'Chile', coordinates: [-71.543, -35.6751] },
  { name: 'United Kingdom', coordinates: [-3.436, 55.3781] },
  { name: 'Fiji', coordinates: [178.065, -17.7134] },
  { name: 'Slovenia', coordinates: [14.9955, 46.1512] },
  { name: 'South Africa', coordinates: [22.9375, -30.5595] },
  { name: 'France', coordinates: [2.2137, 46.2276] },
  { name: 'Australia', coordinates: [133.7751, -25.2744] },
  { name: 'South Korea', coordinates: [127.7669, 35.9078] },
  { name: 'United States of America', coordinates: [-95.7129, 37.0902] },
  { name: 'Canada', coordinates: [-106.3468, 56.1304] },
  { name: 'Vietnam', coordinates: [108.2772, 14.0583] },
  { name: 'New Zealand', coordinates: [174.886, -40.9006] },
  { name: 'Ethiopia', coordinates: [40.4897, 9.145] },
  { name: 'Mexico', coordinates: [-102.5528, 23.6345] },
  { name: 'Federated States of Micronesia', coordinates: [158.2151, 6.8875] },
  { name: 'Cambodia', coordinates: [104.991, 12.5657] },
  { name: 'Thailand', coordinates: [100.9925, 15.87] },
  { name: 'Taiwan', coordinates: [120.9605, 23.6978] },
  { name: 'Spain', coordinates: [-3.7492, 40.4637] },
  { name: 'Iceland', coordinates: [-19.0208, 64.9631] },
  { name: 'Japan', coordinates: [138.2529, 36.2048] }
];

const toGeoJson = (countries) => ({
  type: 'FeatureCollection',
  features: countries.map((country) => ({
    type: 'Feature',
    geometry: {
      type: 'Point',
      coordinates: country.coordinates
    },
    properties: {
      name: country.name
    }
  }))
});

const sourceId = 'conference-countries-source';
const glowLayerId = 'conference-countries-glow';
const pinLayerId = 'conference-countries-pin';

const isEmbeddedInIframe = () => {
  try {
    return window.self !== window.top;
  } catch (error) {
    return true;
  }
};

const ConferenceCountriesMap = () => {
  const mapRef = useRef();
  const mapContainerRef = useRef();
  const embeddedInIframe = isEmbeddedInIframe();
  const [currentStyle, setCurrentStyle] = useState(0);
  const [maximumHeight, setMaximumHeight] = useState(600);

  const onPinMouseEnter = useCallback(() => {
    mapRef.current.getCanvas().style.cursor = 'pointer';
  }, []);

  const onPinMouseLeave = useCallback(() => {
    mapRef.current.getCanvas().style.cursor = '';
  }, []);

  const removeCountryLayers = useCallback(() => {
    if (!mapRef.current) {
      return;
    }

    if (mapRef.current.getLayer(glowLayerId)) {
      mapRef.current.removeLayer(glowLayerId);
    }

    if (mapRef.current.getLayer(pinLayerId)) {
      mapRef.current.removeLayer(pinLayerId);
    }

    if (mapRef.current.getSource(sourceId)) {
      mapRef.current.removeSource(sourceId);
    }
  }, []);

  const addCountryLayers = useCallback(() => {
    if (!mapRef.current) {
      return;
    }

    const sourceData = toGeoJson(conferenceCountries);

    mapRef.current.addSource(sourceId, {
      type: 'geojson',
      data: sourceData
    });

    mapRef.current.addLayer({
      id: glowLayerId,
      type: 'circle',
      source: sourceId,
      paint: {
        'circle-color': '#f39c12',
        'circle-radius': 11,
        'circle-opacity': 0.28,
        'circle-blur': 0.6
      }
    });

    mapRef.current.addLayer({
      id: pinLayerId,
      type: 'circle',
      source: sourceId,
      paint: {
        'circle-color': '#ffad00',
        'circle-radius': 6,
        'circle-stroke-width': 2,
        'circle-stroke-color': '#fff7df'
      }
    });

    mapRef.current.off('mouseenter', pinLayerId, onPinMouseEnter);
    mapRef.current.off('mouseleave', pinLayerId, onPinMouseLeave);

    mapRef.current.on('mouseenter', pinLayerId, onPinMouseEnter);
    mapRef.current.on('mouseleave', pinLayerId, onPinMouseLeave);
  }, [onPinMouseEnter, onPinMouseLeave]);

  const toggleStyle = () => {
    const newStyle = currentStyle === 0 ? 1 : 0;
    setCurrentStyle(newStyle);

    mapRef.current.once('style.load', () => {
      addCountryLayers();
    });

    mapRef.current.setStyle(mapStyles[newStyle]);
  };

  useEffect(() => {
    setMaximumHeight(embeddedInIframe ? window.innerHeight : window.innerHeight / 2);

    mapboxgl.accessToken = 'pk.eyJ1Ijoic2FmZXQtZmlzaCIsImEiOiJjbWI4aDdlM3IwN3BoMmpzYjB4NnJwZXUyIn0.P0Ty8uA6pxsw8xzrD-AbXA';
    mapRef.current = new mapboxgl.Map({
      container: mapContainerRef.current,
      style: mapStyles[0],
      center: [20, 18],
      zoom: 1.25,
      cooperativeGestures: true
    });

    mapRef.current.on('load', () => {
      addCountryLayers();
    });

    return () => {
      removeCountryLayers();
      mapRef.current.remove();
    };
  }, [addCountryLayers, embeddedInIframe, removeCountryLayers]);

  return (
    <Box
      sx={{
        position: 'relative',
        height: embeddedInIframe ? '100vh' : '50vh',
        maxHeight: embeddedInIframe ? 'none' : maximumHeight
      }}
    >
      <div id='conference-map-container' ref={mapContainerRef} />
      <IconButton onClick={toggleStyle} sx={{ position: 'absolute', top: '10px', left: '10px', zIndex: '1' }}>
        <BiLayer size='1.0em' />
      </IconButton>
    </Box>
  );
};

export default ConferenceCountriesMap;
